import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import { CLASSES } from "../data/classes";
import { isClassUnlocked, CLASS_UNLOCK_LEVEL } from "../data/profile";
import { Loader2, Swords, Trophy, Skull, ArrowLeft, Zap, Heart, Lock, Crown } from "lucide-react";
import { SFX } from "../utils/audio";
import { SceneBackdrop } from "../components/game/SceneBackdrop";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper: floating damage number that drifts upward and fades
const FloatingDmg = ({ x, y, value, crit, side, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 1100);
    return () => clearTimeout(t);
  }, [onDone]);
  const color = crit ? "text-yellow-300" : "text-rose-400";
  const size = crit ? "text-4xl" : "text-2xl";
  return (
    <div
      data-testid="float-dmg"
      className={`absolute z-30 font-display font-bold ${color} ${size} pointer-events-none select-none`}
      style={{
        left: `${x}%`,
        top: `${y}%`,
        textShadow: "3px 3px 0 #000, 0 0 12px rgba(0,0,0,0.7)",
        animation: "drift-up 1.1s ease-out forwards",
      }}
    >
      -{value}{crit ? "!" : ""}
    </div>
  );
};

const PvPDuel = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user, loginWithGoogle } = useAuth();
  const { profile } = useProfile();
  const [match, setMatch] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef(null);
  const [readyDeadlineLeft, setReadyDeadlineLeft] = useState(null);
  const [turnDeadlineLeft, setTurnDeadlineLeft] = useState(null);
  const [floatingDmg, setFloatingDmg] = useState([]);
  const [hitFlash, setHitFlash] = useState({ me: false, opp: false });
  const prevHpRef = useRef({ you: null, opp: null });

  const stopPoll = () => { if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null; };

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pvp/match/${matchId}`, { withCredentials: true });
      setMatch(res.data);
      if (res.data.status === "finished") stopPoll();
      if (res.data.status === "dungeon") {
        // Hand off to the dungeon page
        stopPoll();
        navigate(`/pvp-dungeon/${matchId}`);
      }
    } catch (e) {
      // 403 / 404 => bail out
      if (e.response?.status === 404 || e.response?.status === 403) {
        toast.error("Match introuvable");
        navigate("/pvp");
      }
    }
  }, [matchId, navigate]);

  useEffect(() => {
    if (!user) return;
    refresh();
    pollRef.current = setInterval(refresh, 1400);
    return stopPoll;
  }, [user, refresh]);

  // Detect HP drops between updates → floating damage + flash
  useEffect(() => {
    if (!match || match.status !== "in_duel") return;
    const cur = { you: match.you?.hp, opp: match.opponent?.hp };
    const prev = prevHpRef.current;
    if (prev.you != null && cur.you != null && cur.you < prev.you) {
      const dmg = prev.you - cur.you;
      const id = Math.random().toString(36).slice(2);
      setFloatingDmg((f) => [...f, { id, value: dmg, crit: dmg >= 15, side: "me", x: 22 + Math.random() * 10, y: 45 + Math.random() * 10 }]);
      setHitFlash((h) => ({ ...h, me: true }));
      setTimeout(() => setHitFlash((h) => ({ ...h, me: false })), 350);
      SFX.hit?.();
    }
    if (prev.opp != null && cur.opp != null && cur.opp < prev.opp) {
      const dmg = prev.opp - cur.opp;
      const id = Math.random().toString(36).slice(2);
      setFloatingDmg((f) => [...f, { id, value: dmg, crit: dmg >= 15, side: "opp", x: 70 + Math.random() * 10, y: 45 + Math.random() * 10 }]);
      setHitFlash((h) => ({ ...h, opp: true }));
      setTimeout(() => setHitFlash((h) => ({ ...h, opp: false })), 350);
      SFX.crit?.();
    }
    prevHpRef.current = cur;
  }, [match?.you?.hp, match?.opponent?.hp, match?.status]);

  const removeFloating = (id) => setFloatingDmg((f) => f.filter((x) => x.id !== id));

  // Countdown for ready / turn deadlines
  useEffect(() => {
    const t = setInterval(() => {
      if (!match) return;
      const now = Date.now();
      if (match.status === "waiting_ready" && match.ready_deadline) {
        const left = Math.max(0, Math.floor((new Date(match.ready_deadline).getTime() - now) / 1000));
        setReadyDeadlineLeft(left);
      } else { setReadyDeadlineLeft(null); }
      if (match.status === "in_duel" && match.turn_deadline) {
        const left = Math.max(0, Math.floor((new Date(match.turn_deadline).getTime() - now) / 1000));
        setTurnDeadlineLeft(left);
      } else { setTurnDeadlineLeft(null); }
    }, 500);
    return () => clearInterval(t);
  }, [match]);

  const submitReady = async () => {
    if (!chosen) return;
    const cls = CLASSES[chosen];
    if (!cls) return;
    setSubmitting(true);
    try {
      // We send the BASE stats (level-based bonuses already in baseStats for now)
      const baseStats = { ...cls.baseStats, pvMax: cls.baseStats.pv };
      const competences = (cls.competences || []).map((c) => ({
        id: c.id,
        nom: c.nom,
        icon: c.icon,
        cout: c.cout,
        description: c.description,
        degats: c.degats || 5,
        effet: c.effet || "",
      }));
      const res = await axios.post(`${API}/pvp/match/${matchId}/ready`, {
        classe_id: chosen, base_stats: baseStats, competences,
      }, { withCredentials: true });
      setMatch(res.data);
      SFX.click();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  const submitAction = async (competence_id) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/pvp/match/${matchId}/action`, { action: "skill", competence_id }, { withCredentials: true });
      setMatch(res.data);
      SFX.hit?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  const submitForfeit = async () => {
    if (!window.confirm("Abandonner et perdre 25 pts ?")) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/pvp/match/${matchId}/action`, { action: "forfeit" }, { withCredentials: true });
      setMatch(res.data);
    } catch (e) {} finally { setSubmitting(false); }
  };

  if (!user) {
    return (
      <div className="h-screen w-screen bg-slate-900 flex items-center justify-center text-white">
        <div className="text-center">
          <p className="mb-3">Connecte-toi pour rejoindre le duel.</p>
          <button onClick={loginWithGoogle} className="bg-white text-slate-950 px-3 py-2 rounded-lg font-bold">Connexion</button>
        </div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="h-screen w-screen bg-slate-900 flex items-center justify-center text-white">
        <Loader2 className="w-12 h-12 animate-spin" />
      </div>
    );
  }

  // FINISHED
  if (match.status === "finished") {
    const youWon = match.winner_user_id === user.user_id;
    return (
      <div className="h-screen w-screen bg-slate-900 bg-dots flex items-center justify-center p-4">
        <div className={`max-w-md w-full ${youWon ? "bg-gradient-to-br from-yellow-400 to-amber-500" : "bg-gradient-to-br from-rose-700 to-rose-900"} border-4 border-slate-950 rounded-3xl p-6 cartoon-shadow-lg-static text-center`}>
          {youWon ? <Trophy className="w-20 h-20 mx-auto text-slate-950 mb-3" /> : <Skull className="w-20 h-20 mx-auto text-white mb-3" />}
          <h1 data-testid="pvp-result" className={`font-display text-4xl font-bold mb-2 ${youWon ? "text-slate-950" : "text-white"}`} style={{ textShadow: "3px 3px 0 rgba(0,0,0,0.4)" }}>
            {youWon ? "VICTOIRE !" : "DÉFAITE"}
          </h1>
          <p className={`font-bold mb-4 ${youWon ? "text-slate-900" : "text-rose-200"}`}>
            {youWon ? `+${match.rank_delta} pts de rang` : `-${match.rank_delta} pts de rang`}
          </p>
          <div className="bg-slate-950/30 rounded-xl p-3 text-left mb-4 max-h-40 overflow-y-auto">
            {(match.log || []).slice(-10).map((l, i) => (
              <p key={i} className="text-xs text-slate-100 mb-0.5">• {l}</p>
            ))}
          </div>
          <div className="flex gap-2">
            <button data-testid="pvp-back-menu" onClick={() => navigate("/")} className="flex-1 bg-slate-900 text-white border-4 border-slate-950 rounded-xl py-2 font-display font-bold uppercase text-sm">Menu</button>
            <button data-testid="pvp-rematch" onClick={() => navigate("/pvp")} className="flex-1 bg-emerald-500 text-white border-4 border-slate-950 rounded-xl py-2 font-display font-bold uppercase text-sm">Re-jouer</button>
          </div>
        </div>
      </div>
    );
  }

  // WAITING_READY — pick a class
  if (match.status === "waiting_ready") {
    const playerLevel = profile?.level || 1;
    const myReady = !!match.you?.ready;
    const oppReady = !!match.opponent?.ready;
    return (
      <div className="h-screen w-screen bg-slate-900 bg-dots overflow-y-auto">
        <div className="max-w-5xl mx-auto p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <button onClick={submitForfeit} className="bg-rose-500 text-white border-4 border-slate-950 rounded-xl px-3 py-2 cartoon-shadow flex items-center gap-2 text-sm">
              <ArrowLeft className="w-4 h-4" /> Abandonner
            </button>
            <h1 className="font-display text-2xl font-bold text-white flex items-center gap-2"><Swords className="w-6 h-6 text-rose-400" /> Préparation</h1>
            <div data-testid="ready-timer" className="bg-yellow-400 text-slate-950 border-4 border-slate-950 rounded-xl px-3 py-2 cartoon-shadow font-display font-bold text-sm">
              ⏱ {readyDeadlineLeft != null ? `${readyDeadlineLeft}s` : "—"}
            </div>
          </div>

          {/* VS panel */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <PlayerBadge p={match.you} isMe ready={myReady} />
            <PlayerBadge p={match.opponent} ready={oppReady} />
          </div>

          <h2 className="font-display font-bold text-base mb-2 text-yellow-400">Choisis ta classe</h2>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
            {Object.values(CLASSES).map((c) => {
              const unlocked = isClassUnlocked(c.id, playerLevel);
              const isPicked = chosen === c.id;
              return (
                <button
                  key={c.id}
                  data-testid={`pvp-pick-${c.id}`}
                  onClick={() => unlocked && setChosen(c.id)}
                  disabled={!unlocked || myReady}
                  className={`relative bg-slate-800 border-4 ${isPicked ? "border-yellow-400" : "border-slate-950"} rounded-xl p-2 transition-transform hover:-translate-y-0.5 ${!unlocked ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <div className={`w-full aspect-square rounded-lg bg-gradient-to-br ${c.couleur} border-2 border-slate-950 overflow-hidden mb-1 relative`}>
                    <img src={c.image} alt={c.nom} className={`w-full h-full object-cover ${!unlocked ? "grayscale brightness-50" : ""}`} />
                    {!unlocked && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950/60">
                        <Lock className="w-6 h-6 text-yellow-300" />
                        <span className="text-yellow-300 text-[10px] font-bold">Niv. {CLASS_UNLOCK_LEVEL[c.id]}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] font-bold leading-tight">{c.nom.replace("Le ", "").replace("L'", "")}</p>
                </button>
              );
            })}
          </div>

          <button
            data-testid="pvp-ready"
            onClick={submitReady}
            disabled={!chosen || myReady || submitting}
            className={`w-full font-display font-bold uppercase tracking-wider py-3 rounded-2xl border-4 border-slate-950 ${myReady ? "bg-emerald-500 text-white" : chosen ? "bg-yellow-400 text-slate-950 cartoon-shadow" : "bg-slate-700 text-slate-400 cursor-not-allowed"}`}
          >
            {myReady ? "✅ Prêt — en attente de l'adversaire…" : submitting ? "Envoi…" : chosen ? "Confirmer la classe" : "Choisis une classe d'abord"}
          </button>
        </div>
      </div>
    );
  }

  // IN_DUEL — turn-based combat
  const you = match.you;
  const opp = match.opponent;
  const isMyTurn = match.current_turn_user_id === you.user_id;
  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden relative">
      <SceneBackdrop sceneClass="scene-celestial" particle="✨" count={10} overlayClass="combat-lumiere" />
      {/* Floating damage numbers */}
      {floatingDmg.map((f) => (
        <FloatingDmg key={f.id} {...f} onDone={() => removeFloating(f.id)} />
      ))}
      <div className="relative z-10 flex flex-col h-full">
        {/* Header */}
        <div className="bg-slate-950/70 px-3 py-2 border-b-4 border-slate-950 flex items-center justify-between gap-2">
          <button onClick={submitForfeit} className="bg-rose-500 text-white border-2 border-slate-950 rounded-lg px-2 py-1 text-xs font-bold">Abandonner</button>
          <div className="text-center">
            <p className="font-display font-bold text-white text-sm flex items-center gap-1 justify-center">
              <Swords className="w-4 h-4 text-rose-400" /> Duel — Tour de <span className="text-yellow-400">{isMyTurn ? "toi" : opp?.name}</span>
            </p>
            <p className="text-[10px] text-slate-400" data-testid="turn-timer">⏱ {turnDeadlineLeft != null ? `${turnDeadlineLeft}s` : "—"}</p>
          </div>
          <div className="w-16" />
        </div>

        {/* Combatants */}
        <div className="flex-1 grid grid-cols-2 gap-2 p-3">
          <CombatCard p={you} isMe glowMyTurn={isMyTurn} hitFlash={hitFlash.me} />
          <CombatCard p={opp} glowMyTurn={!isMyTurn} flipped hitFlash={hitFlash.opp} />
        </div>

        {/* Skill bar */}
        <div className="bg-slate-950/80 border-t-4 border-slate-950 p-3">
          <div className="flex items-center justify-between mb-2 text-xs">
            <span className="text-white font-bold flex items-center gap-1"><Zap className="w-4 h-4 text-sky-400" /> Énergie : {you.energy}/{you.energy_max}</span>
            <span data-testid="turn-status" className={`font-bold ${isMyTurn ? "text-emerald-400" : "text-slate-500"}`}>
              {isMyTurn ? "À TOI DE JOUER" : "EN ATTENTE…"}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {(you.competences || []).map((c) => {
              const canCast = isMyTurn && you.energy >= c.cout && !submitting;
              return (
                <button
                  key={c.id}
                  data-testid={`pvp-skill-${c.id}`}
                  onClick={() => submitAction(c.id)}
                  disabled={!canCast}
                  className={`text-left bg-slate-800 border-4 border-slate-950 rounded-xl p-2 cartoon-shadow text-white ${canCast ? "hover:-translate-y-0.5" : "opacity-50 cursor-not-allowed"}`}
                  title={c.description}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xl">{c.icon}</span>
                    <span className="text-[10px] font-bold text-sky-400">{c.cout}⚡</span>
                  </div>
                  <p className="text-xs font-display font-bold leading-tight mt-1">{c.nom}</p>
                  <p className="text-[10px] text-slate-400">{c.degats} base</p>
                </button>
              );
            })}
          </div>

          {/* Combat log */}
          <div data-testid="pvp-log" className="mt-2 bg-slate-900 rounded-xl border-2 border-slate-950 px-2 py-1 max-h-20 overflow-y-auto">
            {(match.log || []).slice(-6).map((l, i) => (
              <p key={i} className="text-[11px] text-slate-200">• {l}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const PlayerBadge = ({ p, isMe = false, ready }) => {
  if (!p) return null;
  const cls = p.classe_id ? CLASSES[p.classe_id] : null;
  return (
    <div data-testid={`pvp-badge-${isMe ? "me" : "opp"}`} className={`bg-slate-800 border-4 ${ready ? "border-emerald-400" : "border-slate-950"} rounded-2xl p-3 cartoon-shadow-lg-static`}>
      <div className="flex items-center gap-2">
        {p.picture && <img src={p.picture} alt="" className="w-10 h-10 rounded-full border-2 border-slate-950" />}
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-sm text-white truncate">{p.name}{isMe ? " (toi)" : ""}</p>
          <p className="text-[10px] text-yellow-400 font-bold">{p.rank_points} pts</p>
        </div>
        {ready ? <Crown className="w-4 h-4 text-emerald-400" /> : null}
      </div>
      {cls && (
        <div className="mt-2 flex items-center gap-2">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${cls.couleur} border-2 border-slate-950 overflow-hidden`}>
            <img src={cls.image} alt={cls.nom} className="w-full h-full object-cover" />
          </div>
          <p className="text-xs font-bold text-slate-100">{cls.nom}</p>
        </div>
      )}
      <p className={`mt-1 text-[10px] font-bold ${ready ? "text-emerald-400" : "text-amber-400"}`}>
        {ready ? "✓ Prêt" : "En attente…"}
      </p>
    </div>
  );
};

const CombatCard = ({ p, isMe = false, glowMyTurn = false, flipped = false, hitFlash = false }) => {
  if (!p) return null;
  const cls = p.classe_id ? CLASSES[p.classe_id] : null;
  const pct = p.hp_max ? Math.max(0, Math.min(100, (p.hp / p.hp_max) * 100)) : 0;
  return (
    <div data-testid={`pvp-card-${isMe ? "me" : "opp"}`} className={`bg-slate-900/70 border-4 ${glowMyTurn ? "border-yellow-400 cosmetic-pulse-warm" : "border-slate-950"} rounded-2xl p-3 cartoon-shadow-lg-static flex flex-col items-center transition-transform ${hitFlash ? "animate-pulse scale-95 brightness-200" : ""}`}>
      <div className="flex items-center gap-2 mb-2 w-full">
        {p.picture && <img src={p.picture} alt="" className="w-8 h-8 rounded-full border-2 border-slate-950" />}
        <div className="flex-1">
          <p className="font-display font-bold text-sm text-white">{p.name}{isMe ? " (toi)" : ""}</p>
          <p className="text-[10px] text-yellow-400 font-bold">{p.rank_points} pts</p>
        </div>
      </div>
      {cls && (
        <div className={`w-32 h-32 rounded-2xl bg-gradient-to-br ${cls.couleur} border-4 border-slate-950 overflow-hidden mb-2 ${flipped ? "scale-x-[-1]" : ""}`}>
          <img src={cls.image} alt={cls.nom} className="w-full h-full object-cover" />
        </div>
      )}
      <p className="text-xs font-bold text-slate-100 mb-1">{cls?.nom}</p>
      <div className="w-full bg-slate-950 border-2 border-slate-950 rounded-full h-4 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs font-bold mt-1 text-emerald-400 flex items-center gap-1"><Heart className="w-3 h-3" /> {p.hp} / {p.hp_max}</p>
    </div>
  );
};

export default PvPDuel;
